<template>
  <div class="payment">
    <div class="payment-content" v-if="order">
      <el-card class="box-card" shadow="hover">
        <div slot="header" class="payment-clearfix">
          <span>UPI Payment</span>
        </div>
        <div class="payment-item">
          <div class="payment-amount-section">
            <span class="label">Amount to Pay</span>
            <span class="amount">{{totalPrice}}₹</span>
          </div>

          <!-- 支付通道选择：NSDL (SveltPay) / Jana VPA (DeepPay) -->
          <div class="channel-select" v-if="!activeChannel">
            <p class="channel-hint">Choose your payment channel</p>
            <div class="channel-grid">
              <div class="channel-card" @click="chooseChannel('nsdl')">
                <div class="channel-logo nsdl">NSDL</div>
                <p class="channel-name">NSDL</p>
                <p class="channel-desc">UPI · NSDL Payments Bank</p>
              </div>
              <div class="channel-card" @click="chooseChannel('jana')">
                <div class="channel-logo jana">JANA</div>
                <p class="channel-name">Jana VPA</p>
                <p class="channel-desc">UPI · Jana Small Finance Bank</p>
              </div>
            </div>
          </div>

          <!-- 创单加载中 -->
          <div class="loading-section" v-else-if="loading">
            <i class="el-icon-loading"></i>
            <p>Generating payment code via {{channelName}}...</p>
          </div>

          <!-- 深链支付：QR + 快捷钱包 + 轮询 -->
          <div class="qr-section" v-else-if="payLink">
            <p class="channel-tag">{{channelName}}</p>
            <p class="qr-hint">Scan the QR code with any UPI app</p>
            <div class="qr-code">
              <img :src="qrUrl" alt="UPI QR Code" />
            </div>
            <div class="payment-info">
              <p>Order: {{order.order_num}}</p>
              <p v-if="mchOrderNo">MchOrderNo: {{mchOrderNo}}</p>
            </div>

            <!-- 快捷钱包打开方式（仅 upi:// 深链支持） -->
            <div class="wallet-buttons" v-if="isUpiDeepLink">
              <p class="wallet-hint">Or open your UPI wallet directly:</p>
              <div class="wallet-grid">
                <button class="wallet-btn gpay" @click="openGpay">G Pay</button>
                <button class="wallet-btn phonepe" @click="openPhonePe">PhonePe</button>
                <button class="wallet-btn paytm" @click="openPaytm">Paytm</button>
                <button class="wallet-btn other" @click="openOtherUpi">Other UPI</button>
              </div>
            </div>

            <div class="payment-steps">
              <p><i class="el-icon-mobile-phone"></i> Open any UPI app (Google Pay, PhonePe, Paytm)</p>
              <p><i class="el-icon-scan"></i> Scan the QR code above or tap your wallet</p>
              <p><i class="el-icon-check"></i> Complete payment</p>
            </div>
            <div class="polling-status" v-if="polling">
              <i class="el-icon-loading"></i>
              <span>Waiting for payment confirmation...</span>
            </div>
            <div class="pay-actions">
              <el-button type="primary" @click="handlePaymentDone" :loading="payDoneLoading">Payment Done</el-button>
              <el-button @click="resetChannel">Change Channel</el-button>
            </div>
          </div>

          <!-- 创单失败 -->
          <div class="error-section" v-else>
            <p>Failed to generate payment code. Please try again.</p>
            <el-button type="primary" @click="retryInit">Retry</el-button>
            <el-button @click="resetChannel">Change Channel</el-button>
          </div>
        </div>
      </el-card>
    </div>
    <div class="not-found" v-else>Unable to find the order</div>
  </div>
</template>
<script>
import * as ordersAPI from '@/api/orders'
import * as paymentAPI from '@/api/payment'
export default {
  name: 'Payment',
  data() {
    return {
      orderNum: '',
      order: null,
      payLink: '',
      activeChannel: '',
      mchOrderNo: '',
      loading: false,
      polling: false,
      pollTimer: null,
      payDoneLoading: false
    }
  },
  computed: {
    totalPrice() {
      if (!this.order) return 0
      return this.order.discount_price * this.order.num
    },
    qrUrl() {
      const link = this.normalizedPayLink
      if (!link) return ''
      return (
        'https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=' +
        encodeURIComponent(link)
      )
    },
    // 规范化深链：网关返回的深链若被 URL 编码（%3A%2F%2F 等）先解码，保证 QR 与钱包唤起地址正确
    normalizedPayLink() {
      if (!this.payLink) return ''
      try {
        const decoded = decodeURIComponent(this.payLink)
        if (decoded !== this.payLink && /^(upi|http|https):\/\//.test(decoded)) {
          return decoded
        }
      } catch (e) {
        // 解码失败保持原样
      }
      return this.payLink
    },
    // upi:// 深链才支持快捷钱包唤起（gpay/phonepe/paytm 转换）
    isUpiDeepLink() {
      return this.normalizedPayLink.indexOf('upi://pay?') === 0
    },
    channelName() {
      return this.activeChannel === 'nsdl' ? 'NSDL' : 'Jana VPA'
    }
  },
  activated() {
    if (this.$route.query.orderNum != undefined) {
      this.orderNum = this.$route.query.orderNum
    }
  },
  watch: {
    orderNum: function() {
      this.load()
    }
  },
  methods: {
    load() {
      ordersAPI
        .showOrder(this.orderNum)
        .then(res => {
          if (res.status === 200) {
            this.order = res.data
            if (this.order.type === 2) {
              this.notifySucceed('Order already paid')
              this.$router.push({ path: '/order' })
              return
            }
          } else if (res.status === 20001) {
            this.loginExpired(res.msg)
          } else {
            this.notifyError('Failed to obtain order', res.msg)
          }
        })
        .catch(err => {
          this.notifyError('Failed to obtain order', err)
        })
    },
    // 用户点击通道入口
    chooseChannel(channel) {
      this.activeChannel = channel
      this.payLink = ''
      this.mchOrderNo = ''
      this.initPayment()
    },
    // 回到通道选择页
    resetChannel() {
      this.stopPolling()
      this.activeChannel = ''
      this.payLink = ''
      this.mchOrderNo = ''
      this.loading = false
    },
    retryInit() {
      this.initPayment()
    },
    // 根据已选通道调用对应创单接口
    initPayment() {
      this.loading = true
      this.payLink = ''
      if (this.activeChannel === 'nsdl') {
        this.initSveltPay()
      } else {
        this.initDeepPay()
      }
    },
    // NSDL：SveltPay 创单 → wallet_link (upi://pay?...@nsdlpbma)
    initSveltPay() {
      paymentAPI
        .initSveltPayment(String(this.order.order_num))
        .then(res => {
          this.loading = false
          if (res.status === 200 && res.data && res.data.wallet_link) {
            this.payLink = res.data.wallet_link
            this.polling = true
            this.startPolling()
          } else if (res.status === 20001) {
            this.loginExpired(res.msg)
          } else {
            this.notifyError('Failed to initiate payment', res.msg)
          }
        })
        .catch(err => {
          this.loading = false
          this.notifyError('Failed to initiate payment', err)
        })
    },
    // Jana VPA：DeepPay 创单 → pay_url
    initDeepPay() {
      paymentAPI
        .initOnlinePayment({
          order_num: String(this.order.order_num),
          amount: String(this.totalPrice),
          nickname: this.$store.getters.getUser.nickname || 'Customer',
          phone: this.order.address_phone || '',
          email: this.order.address_email || ''
        })
        .then(res => {
          this.loading = false
          if (res.status === 200 && res.data && res.data.pay_url) {
            this.payLink = res.data.pay_url
            this.mchOrderNo = res.data.mch_order_no
            this.polling = true
            this.startPolling()
          } else if (res.status === 20001) {
            this.loginExpired(res.msg)
          } else {
            this.notifyError('Failed to initiate payment', res.msg)
          }
        })
        .catch(err => {
          this.loading = false
          this.notifyError('Failed to initiate payment', err)
        })
    },
    // 共用：通过深链唤起 UPI 钱包（仅移动端有效）
    openWallet(link) {
      if (!link) {
        this.notifyError('Payment link not ready')
        return
      }
      window.location.href = link
      this.notifySucceed('UPI app opened. Please complete payment.')
    },
    // GPay：upi://pay? -> gpay://upi/pay?
    openGpay() {
      this.openWallet(this.normalizedPayLink.replace('upi://pay?', 'gpay://upi/pay?'))
    },
    // PhonePe：upi://pay? -> phonepe://pay?
    openPhonePe() {
      this.openWallet(this.normalizedPayLink.replace('upi://pay?', 'phonepe://pay?'))
    },
    // Paytm：upi://pay? -> paytmmp://cash_wallet?featuretype=money_transfer&
    openPaytm() {
      this.openWallet(
        this.normalizedPayLink.replace(
          'upi://pay?',
          'paytmmp://cash_wallet?featuretype=money_transfer&'
        )
      )
    },
    // 其他 UPI 应用：使用原始 upi:// 深链
    openOtherUpi() {
      this.openWallet(this.normalizedPayLink)
    },
    // Payment Done：向后端确认订单已支付（status 200/10011 均为已支付）
    handlePaymentDone() {
      this.payDoneLoading = true
      ordersAPI
        .payOrder(String(this.order.order_num))
        .then(res => {
          this.payDoneLoading = false
          if (res.status === 200 || res.status === 10011) {
            this.stopPolling()
            this.notifySucceed('Payment successful!')
            setTimeout(() => {
              this.$router.push({ path: '/order' })
            }, 1500)
          } else {
            this.notifyError('Payment not received', 'Please complete payment via UPI first')
          }
        })
        .catch(err => {
          this.payDoneLoading = false
          this.notifyError('Payment verification failed', err)
        })
    },
    startPolling() {
      this.pollTimer = setInterval(() => {
        ordersAPI
          .showOrder(this.order.order_num)
          .then(res => {
            if (res.data && res.data.type === 2) {
              clearInterval(this.pollTimer)
              this.polling = false
              this.notifySucceed('Payment successful!')
              setTimeout(() => {
                this.$router.push({ path: '/order' })
              }, 1500)
            }
          })
          .catch(() => {})
      }, 5000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.polling = false
    }
  },
  beforeDestroy() {
    this.stopPolling()
  }
}
</script>

<style scoped>
.payment .payment-content {
  width: 600px;
  margin: 0 auto;
  margin-bottom: 50px;
  margin-top: 50px;
}
.payment .payment-content .payment-item {
  text-align: center;
}
.payment-amount-section {
  margin-bottom: 24px;
  padding: 20px;
  background: #f0edea;
  border-radius: 12px;
}
.payment-amount-section .label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}
.payment-amount-section .amount {
  display: block;
  font-size: 36px;
  font-weight: 700;
  color: #b9365b;
}
/* 通道选择 */
.channel-select .channel-hint {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}
.channel-grid {
  display: flex;
  justify-content: center;
  gap: 24px;
}
.channel-card {
  width: 200px;
  padding: 24px 16px;
  border: 2px solid #f0edea;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
}
.channel-card:hover {
  border-color: #b9365b;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}
.channel-logo {
  width: 72px;
  height: 72px;
  margin: 0 auto 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
}
.channel-logo.nsdl {
  background: #005baa;
}
.channel-logo.jana {
  background: #e8862d;
}
.channel-name {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}
.channel-desc {
  font-size: 12px;
  color: #999;
  line-height: 18px;
}
/* 深链支付区 */
.qr-section {
  text-align: center;
}
.channel-tag {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: #b9365b;
  margin-bottom: 12px;
}
.qr-hint {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}
.qr-code {
  margin: 0 auto 20px;
  width: 250px;
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 2px solid #f0edea;
  border-radius: 12px;
}
.qr-code img {
  width: 220px;
  height: 220px;
}
.payment-info {
  margin: 16px 0;
  font-size: 13px;
  color: #888;
}
/* 快捷钱包按钮 */
.wallet-buttons {
  margin: 16px 0;
}
.wallet-hint {
  font-size: 13px;
  color: #888;
  margin-bottom: 10px;
}
.wallet-grid {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}
.wallet-btn {
  min-width: 92px;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
}
.wallet-btn:hover {
  opacity: 0.85;
}
.wallet-btn.gpay {
  background: #4285f4;
}
.wallet-btn.phonepe {
  background: #5f259f;
}
.wallet-btn.paytm {
  background: #00baf2;
}
.wallet-btn.other {
  background: #607d8b;
}
.payment-steps {
  text-align: left;
  background: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
}
.payment-steps p {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.payment-steps p:last-child {
  margin-bottom: 0;
}
.payment-steps i {
  color: #b9365b;
  font-size: 16px;
}
.polling-status {
  margin-top: 16px;
  color: #b9365b;
  font-size: 14px;
}
.polling-status i {
  margin-right: 8px;
}
.pay-actions {
  margin-top: 20px;
}
.loading-section {
  padding: 40px 0;
  text-align: center;
}
.loading-section i {
  font-size: 48px;
  color: #b9365b;
}
.loading-section p {
  margin-top: 16px;
  color: #666;
}
.error-section {
  padding: 40px 0;
  text-align: center;
  color: #666;
}
.not-found {
  height: 500px;
  text-align: center;
  padding-top: 100px;
  color: #666;
}
</style>
